import React from 'react';
import { Alert, Button, Flex, Form, Input, Modal, Select } from 'antd';
import AdminFieldInput from './AdminFieldInput';
import { EFieldType, FIELD_TYPE_OPTIONS, coerceValueForType, defaultValueForType } from '../../../../domain/enums/admin/AdminFieldType';

interface NewFieldFormValues {
  key: string;
  type: EFieldType;
  value: unknown;
}

const INITIAL_VALUES: NewFieldFormValues = {
  key: '',
  type: EFieldType.STRING,
  value: '',
};

interface AdminNewFieldModalProps {
  open: boolean;
  onCancel: () => void;
  notAllowedKeys: string[];
  onAdded: (key: string, value: unknown) => void;
}

const AdminNewFieldModal: React.FC<AdminNewFieldModalProps> = ({ open, onCancel, notAllowedKeys, onAdded }) => {
  const [form] = Form.useForm<NewFieldFormValues>();
  
  const type = (Form.useWatch('type', form) ?? EFieldType.STRING) as EFieldType; // Useeffect equivalent on antdform

  const isStructural = type === EFieldType.OBJECT || type === EFieldType.ARRAY;

  const handleTypeChange = (next: EFieldType) => {
    form.setFieldValue('value', defaultValueForType(next));
  };

  const handleFinish = ({ key, type, value }: NewFieldFormValues) => {
    const structural = type === EFieldType.OBJECT || type === EFieldType.ARRAY;
    const finalValue = structural ? defaultValueForType(type) : coerceValueForType(type, value);
    onAdded(key.trim(), finalValue);
    form.resetFields();
  };

  return (
    <Modal
      title="Ajouter un champ"
      open={open}
      onCancel={onCancel}
      footer={null}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        initialValues={INITIAL_VALUES}
      >
        <Form.Item
          name="key"
          label="Clé"
          extra="Identifiant technique du champ (la clé JSON)."
          rules={[
            { required: true, message: 'Veuillez entrer une clé' },
            {
              validator: (_, value) =>
                value && notAllowedKeys.includes(value.trim())
                  ? Promise.reject(new Error('Cette clé existe déjà'))
                  : Promise.resolve(),
            },
          ]}
        >
          <Input placeholder="ex: api_base_url" autoComplete="off" />
        </Form.Item>

        <Form.Item name="type" label="Type" rules={[{ required: true, message: 'Veuillez choisir un type' }]}>
          <Select options={FIELD_TYPE_OPTIONS} onChange={handleTypeChange} />
        </Form.Item>

        {isStructural ? (
          <Form.Item label="Valeur">
            <Alert
              type="info"
              showIcon
              message={`Le champ sera créé vide (${type === EFieldType.OBJECT ? '{}' : '[]'}) puis éditable directement dans l'onglet.`}
            />
          </Form.Item>
        ) : (
          <AdminFieldInput type={type} namePath={['value']} label="Valeur" />
        )}

        <Flex justify="flex-end" gap={8}>
          <Button onClick={onCancel}>Annuler</Button>
          <Button type="primary" htmlType="submit">Ajouter</Button>
        </Flex>
      </Form>
    </Modal>
  );
};

export default AdminNewFieldModal;
